import type { Component } from "vue"
import { Archive, CircleCheck, UserRoundCheck, UserRoundX, UsersRound } from "lucide-vue-next"
import {
  PatientConversationAwaitingPartyEnum,
  PatientConversationRoleEnum,
  PatientConversationStatusEnum,
  PatientConversationSummaryDTO
} from "@vitable/asclepius-api-client-ts"

/** The one place a wire role (`Provider`, `Support Team`, `Patient`) becomes a display label. */
const ROLE_LABELS: Record<PatientConversationRoleEnum, string> = {
  [PatientConversationRoleEnum.Provider]: "Provider",
  [PatientConversationRoleEnum.SupportTeam]: "Support Team",
  [PatientConversationRoleEnum.Patient]: "Patient"
}

export function roleLabel(role: PatientConversationRoleEnum): string {
  return ROLE_LABELS[role] ?? role
}

/** Section headings of the assignee pickers, in display order. */
export interface ConversationStatusBadge {
  label: string
  tone: "gray" | "green" | "red"
}

/** The chart's status badge: Open stands out in green so follow-ups are easy to spot; parked states are grey. */
export function conversationStatusBadge(status: PatientConversationStatusEnum): ConversationStatusBadge {
  return { label: status, tone: status === PatientConversationStatusEnum.Open ? "green" : "gray" }
}

// `None` has no label: nobody owes a reply
const AWAITING_LABELS: Partial<Record<PatientConversationAwaitingPartyEnum, string>> = {
  [PatientConversationAwaitingPartyEnum.Provider]: "Awaiting provider",
  [PatientConversationAwaitingPartyEnum.Patient]: "Awaiting patient"
}

/** Whose turn it is, worded as the inbox does. Only an Open conversation has a turn (TDP §5.1.3, §4.2.4). */
export function conversationAwaitingLabel(
  conversation: Pick<PatientConversationSummaryDTO, "status" | "awaiting">
): string | undefined {
  if (conversation.status !== PatientConversationStatusEnum.Open || !conversation.awaiting) return undefined
  return AWAITING_LABELS[conversation.awaiting]
}

export function conversationAwaitingBadge(
  conversation: Pick<PatientConversationSummaryDTO, "status" | "awaiting">
): ConversationStatusBadge | undefined {
  const label = conversationAwaitingLabel(conversation)
  return label ? { label, tone: "gray" } : undefined
}

export const STAFF_PICKER_SECTIONS: Array<{ role: PatientConversationRoleEnum; label: string }> = [
  { role: PatientConversationRoleEnum.Provider, label: "PROVIDERS" },
  { role: PatientConversationRoleEnum.SupportTeam, label: "SUPPORT TEAM" }
]

/** The staff inbox views built here; `mentions` ("Pages") arrives with M6. */
export type InboxView = "unassigned" | "assigned_to_me" | "all" | "archived" | "closed"

export interface InboxViewOption {
  view: InboxView
  label: string
  icon: Component
}

export const INBOX_VIEWS: Array<InboxViewOption> = [
  { view: "unassigned", label: "Unassigned", icon: UserRoundX },
  { view: "assigned_to_me", label: "Assigned to me", icon: UserRoundCheck },
  { view: "all", label: "All conversations", icon: UsersRound },
  { view: "archived", label: "Archived", icon: Archive },
  { view: "closed", label: "Closed", icon: CircleCheck }
]

export function isInboxView(value: unknown): value is InboxView {
  return INBOX_VIEWS.some((option) => option.view === value)
}

/** Subjects a staff member can start a conversation with (PRD "Decisions to make" #1). Stored as text. */
export const NEW_MESSAGE_SUBJECTS: ReadonlyArray<string> = [
  "General inquiry",
  "Your lab results",
  "Appointment follow-up",
  "Prescription follow-up",
  "Additional information request"
]
